import React, { useCallback, useEffect, useRef, useState } from 'react';
import StandardGrid from "common/agGrid/StandardGrid";
import { ColDef, CsvExportParams, ExcelExportParams, GetContextMenuItemsParams, GridOptions, MenuItemDef, SortChangedEvent } from "@ag-grid-community/all-modules";
import Permissions from "common/security/permissions";
import usePermission from 'common/security/usePermission';
import { useDispatch, useSelector } from 'react-redux';
import IAppStore from 'app/IAppStore';
import Contract from 'domain/contract';
import notificationService from 'common/notificationService';
import useScreenSize from 'common/ui/useScreenSize';
import { Producer, getProducerDisplayName } from 'features/settings/producers/producer';
import { ColumnBuilder } from 'common/agGrid/columnBuilder';
import { RowMenuButton, handleRowMenuClick } from 'common/agGrid/RowMenuButton';
import { formatDate, formatDecimalPlace } from 'common/formatters';
import { useCustomerWillSellText } from 'common/formatters/useCustomerWillSellText';
import { getPaymentOptionDisplayName } from 'domain/paymentOption';
import { useReactToPrint } from 'react-to-print';
import TableApi from 'common/ui/tableApi';
import { ContractColumn, ContractColumnConfigurations, ContractListColumnsFilter, getColumnBuilder } from './ContractListColumns';
import ValidationError from 'common/validationError';
import * as ContractReduxActions from "./contracts.actions";
import { ColumnCustomization } from 'common/agGrid/columnCustomization';
import { calculateRequiredFuturesPrice } from 'features/offers/priceCalculator';
import SyncContractToErpRetryModal from './SyncContractToErpRetryModal';
import unitHelper from "domain/unitHelper";
import useModalState from "common/ui/useModalState";
import './ContractTable.scss';
import useEventListener from 'common/ui/useEventListener';
import contractTypeHelper from 'domain/contractTypeHelper';
import useTranslators from 'common/translation/useTranslators';
import { localizeOfferContract as localize } from "features/localization/localizeService";
import useUnitConversionRates from 'features/settings/commodities/useUnitConversionRates';
import useOfferings from 'features/settings/offerings/useOfferings';
import useContractColumns from './useContractColumns';
import { usePaymentOptions } from 'features/merchants/usePaymentOptions';
import { AgGridReact } from '@ag-grid-community/react';
import useColumnDefinitions from 'common/agGrid/useColumnDefinitions';
import UiCustomization from 'domain/uiCustomization';
import useMerchantFeature from 'features/merchants/useMerchantFeature';
import { MerchantFeature } from 'domain/merchant';
import { useErpIntegrationLoader } from 'features/merchants/useErpIntegration';
import useActiveMerchant from 'features/merchants/useActiveMerchant';
import useFormFieldSettings from 'features/merchants/useFormFieldSettings';
import { getSetting, FormField } from 'domain/formFieldSetting';
import useContractActionMenu from './useContractActionMenu';
import ContractActions from './ContractActions';

const columnBuilder = new ColumnBuilder<Contract>();

interface ContractTableProps {
    contractTableType: ContractTableType;
    contracts: Contract[];
    columnIds: string[];
    search?: string;
    onCustomerSelected?(customer: Producer): void;
    onSignatureRequested?(contract: Contract): void;
    setTableApi?(api: TableApi): void;
    gridOptions?: GridOptions;
    showQuantityUnits?: boolean;
    uiCustomization: UiCustomization;
}

const defaultColDef: any = {
    sortable: true,
    minWidth: 60
};

type SortDirection = "asc" | "desc";

type SelectedSort = {
    type: ContractTableType;
    colId: string;
    sortOrder: SortDirection;
};

export enum ContractTableType {
    Cash = 1,
    Basis = 2,
    Hta = 3,
    DelayedPrice = 4,
    WithOptions = 5,
    BushelOnly = 7
}

const ContractTable: React.FC<ContractTableProps> = props => {
    const customerWillSellText = useCustomerWillSellText();
    const [selectedSortForStatus, setSelectedSortForStatus] = useState<SelectedSort>(null);

    const resyncState = useModalState<{ contract: Contract, hideRetrySync: boolean }>();
    const userConfiguration = useSelector((m: IAppStore) => m.userState.userConfiguration);
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const customerAccounts = useSelector((m: IAppStore) => m.userState.customerAccounts);
    const activeMerchant = useActiveMerchant();
    const contractActionMenu = useContractActionMenu();

    const myCommodities = useSelector((m: IAppStore) => m.myCommodities);
    const offerings = useOfferings();
    const conversionRates = useUnitConversionRates();
    const paymentOptionsDetails = usePaymentOptions();
    const erpIntegrationLoader = useErpIntegrationLoader();
    const allowContractPricing = erpIntegrationLoader.data?.erpConfiguration?.allowContractPricing;

    const canEditContracts = usePermission(Permissions.EditContracts);
    const canSupervisorEdit = usePermission(Permissions.SupervisorEditContract);
    const canEnterErpId = usePermission(Permissions.EnterErpId);

    const canEsign = useMerchantFeature(MerchantFeature.ESignature);

    const formFieldSettings = useFormFieldSettings();
    const quantityFieldSetting = getSetting(formFieldSettings, FormField.Quantity);
    const isQuantityFieldEnabled = quantityFieldSetting?.isVisible && quantityFieldSetting?.isEditable;

    const dispatch = useDispatch();

    const componentRef = useRef();
    const gridRef = useRef<AgGridReact>();

    const screenSize = useScreenSize();
    const useMobileColumns = screenSize.isAtMost("sm");

    const contractColumns = useContractColumns(useMobileColumns);

    const translators = useTranslators();

    const filter: ContractListColumnsFilter = {
        ...new ContractListColumnsFilter(),
        isCustomerEditAllowed: usePermission(Permissions.ManageProducer),
        onCustomerSelected: props.onCustomerSelected,
        onSignatureRequested: props.onSignatureRequested,
        isCustomer: isCustomer,
        isMobile: useMobileColumns,
        conversionRates: conversionRates,
        translators: translators,
        onContractFlagUpdated: onFlagUpdated,
        onErpIdSelected: onErpIdSelected,
        showQuantityUnits: props.showQuantityUnits,
        merchant: activeMerchant,
        isEsignEnabled: canEsign
    }

    const contractColumnBuilder = getColumnBuilder(filter, props.uiCustomization, customerWillSellText);

    useEventListener('resize', handleResize);

    const columns = useColumnDefinitions(getColumnDefinitions, [props.contractTableType, props.columnIds, conversionRates]);

    useEffect(() => {
        if (props.setTableApi != null) {
            props.setTableApi({
                exportAsCsv: exportAsCsv,
                exportAsExcel: exportAsExcel,
                print: printGrid
            })
        }
    }, [props.contractTableType]);


    const handleGridReady = useCallback(params => {
        if (contractColumns != null) {
            setInitialSortForStatus(props.contractTableType);
        }
        params.api.sizeColumnsToFit();
        params.columnApi.autoSizeColumn("quantity");
    }, []);

    function handleResize() {
        gridRef.current.api.sizeColumnsToFit()
    }

    const printWithReact = useReactToPrint({
        onAfterPrint: () => setNormalMode(),
        content: () => componentRef.current,
    });

    function printGrid() {
        setPrintMode();
        setTimeout(function () {
            printWithReact();
        }, 1000);
    }

    function setPrintMode() {
        gridRef.current.api.setDomLayout('print');
    }

    function setNormalMode() {
        gridRef.current.api?.setDomLayout('autoHeight');
    }

    function exportAsCsv() {
        gridRef.current.api.exportDataAsCsv(setupCsvParams())
    }

    function exportAsExcel() {
        gridRef.current.api.exportDataAsExcel(setupExcelParams())
    }

    function exportCellCallback(params: any) {
        const data = params.node.data;
        const commodity = data?.commodity;
        const showLocalConversions = commodity?.baseUnit.id !== commodity?.localUnit.id;
        switch (params.column.colId) {
            case ContractColumn.deliveryEnd:
            case ContractColumn.deliveryStart:
                return formatDate(params.value);
            case ContractColumn.erpId:
                return data?.erpDisplayId;
            case ContractColumn.customer:
                return getProducerDisplayName(data?.producer);
            case ContractColumn.customerWillSell:
                return customerWillSellText.formatValue(params.value);
            case ContractColumn.lastUpdatedBy:
                return data?.lastUpdatedBy.name;
            case ContractColumn.createdBy:
                return data?.createdBy.name;
            case ContractColumn.quantity:
                return data?.quantityContracted;
            case ContractColumn.cashPrice:
            case ContractColumn.basis:
                return params.value ? params.value.toLocaleString() : '';
            case ContractColumn.fillPrice:
                return params.value == null ? '' : formatDecimalPlace(params.value);
            case ContractColumn.futuresPrice:
                return data.offer == null ? null : calculateRequiredFuturesPrice(data.offer, commodity?.isCashOnly);
            case ContractColumn.myBasis:
                return contractTypeHelper.isHta(data.contractType) ? '' : formatDecimalPlace(params.value);
            case ContractColumn.lastUpdatedDate:
            case ContractColumn.fillTime:
                return formatDate(params.value, "MM/DD/YY LTS");
            case ContractColumn.flag:
                return params.value ? "Yes" : "No";
            case ContractColumn.paymentOption:
                return getPaymentOptionDisplayName(params.value, paymentOptionsDetails);
            case ContractColumn.uom:
                return commodity?.baseUnit.abbreviation;
            case ContractColumn.localQuantity:
                return showLocalConversions ? unitHelper.getLocalQuantity(data?.quantityContracted, commodity, conversionRates, props.uiCustomization.offersContractsQuantityDecimalPrecision) : '';
            case ContractColumn.localUom:
                return showLocalConversions ? commodity?.localUnit.abbreviation : '';
            default:
                return params.value;
        }
    }

    function setupExcelParams(): ExcelExportParams {
        return {
            skipColumnHeaders: false,
            allColumns: false,
            onlySelected: false,
            columnKeys: generateColumnsForExcel(),
            fileName: `${getContractTableTypeName(props.contractTableType)}.xlsx`,
            processHeaderCallback: exportHeaderCallback,
            processCellCallback: exportCellCallback
        };
    }

    function setupCsvParams(): CsvExportParams {
        return {
            skipColumnHeaders: false,
            allColumns: false,
            onlySelected: false,
            columnKeys: generateColumnsForExcel(),
            fileName: `${getContractTableTypeName(props.contractTableType)}.csv`,
            columnSeparator: ',',
            processHeaderCallback: exportHeaderCallback,
            processCellCallback: exportCellCallback
        };
    }

    function generateColumnsForExcel(): string[] {
        const keys = [...props.columnIds];
        const quantityIndex: number = keys.indexOf('quantity');
        if (quantityIndex !== -1) keys.splice(quantityIndex + 1, 0, ContractColumn.uom, ContractColumn.localQuantity, ContractColumn.localUom);
        return keys;
    }

    function exportHeaderCallback(params: any) {
        switch (params.column.colId) {
            case ContractColumn.quantity:
                return 'Base Qty';
            default:
                return params.column.colDef.headerName;
        }
    }

    async function onFlagUpdated(contract: Contract) {
        try {
            await ContractReduxActions.updateContractFlag(dispatch, contract);
        }
        catch (error) {
            notificationService.error(((error as ValidationError).errors ?? ["An unknown error occurred"])[0]);
        }
    }

    async function onErpIdSelected(contract: Contract) {
        resyncState.show({ contract: contract, hideRetrySync: false });
    }

    function getColumnDefinitions(): ColDef[] {
        const configured = contractColumnBuilder.listColumns([...props.columnIds, ContractColumn.uom, ContractColumn.localQuantity, ContractColumn.localUom]);
        const actionMenu = columnBuilder.for("", "", { sortable: false })
            .withWidth(30)
            .withCellRenderer(params => params.node.group || !canManageAccounts(params.node.data) ? null : <RowMenuButton />)
            .onCellClick(event => canManageAccounts(event.node.data) ? handleRowMenuClick(event) : null)
            .build();

        return [actionMenu, ...configured || []];
    }

    function canManageAccounts(contract: Contract) {
        if (!isCustomer || contract.customerAccount == null) {
            return true;
        }
        else {
            return customerAccounts.some(x => x.accountId === contract.customerAccount?.accountId && x.canManage);
        }
    }

    const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): MenuItemDef[] => {

        if (params.node == null) return []; //User right-clicked on empty space

        const contract = params.node.data as Contract;

        return contractActionMenu.listActionsForContract(contract, props.contractTableType).map(item => ({
            name: item.name,
            action: item.action,
            disabled: item.disabled,
            tooltip: item.tooltip
        }));
    }, [canEditContracts, canSupervisorEdit, offerings, isCustomer, userConfiguration, isQuantityFieldEnabled, canEnterErpId]);

    const onSortChanged = useCallback(function (ev: SortChangedEvent) {
        const columnState = ev.columnApi.getColumnState();
        const sortedColumn = columnState.find(m => m.sort != null);
        if (sortedColumn && selectedSortForStatus != null && selectedSortForStatus.type === props.contractTableType && (selectedSortForStatus.colId !== sortedColumn.colId || selectedSortForStatus.sortOrder !== sortedColumn.sort)) {
            const config = { ...contractColumns };
            const selectedSort: SelectedSort = {
                colId: sortedColumn.colId,
                sortOrder: sortedColumn.sort,
                type: props.contractTableType
            };
            setSortOrderForType(config, selectedSort);

            //we don't allow customization of mobile columns
            if (useMobileColumns) return;

            ContractReduxActions.saveContractColumnConfigurations(dispatch, config);
            setSelectedSortForStatus(selectedSort);
        }
        ev.columnApi.autoSizeColumn("quantity");
    }, [props.contractTableType])

    function setInitialSortForStatus(contractTableType: ContractTableType) {
        const initialSort: SelectedSort = {
            colId: "id",
            sortOrder: "desc",
            type: contractTableType
        };

        let contractConfig: ColumnCustomization = null;
        switch (props.contractTableType) {
            case ContractTableType.Basis:
                contractConfig = contractColumns.basis;
                break;
            case ContractTableType.Hta:
                contractConfig = contractColumns.hta;
                break;
            case ContractTableType.Cash:
                contractConfig = contractColumns.cash;
                break;
            case ContractTableType.DelayedPrice:
                contractConfig = contractColumns.delayedPrice;
                break;
            case ContractTableType.WithOptions:
                contractConfig = contractColumns.withOptions;
                break;
            case ContractTableType.BushelOnly:
                contractConfig = contractColumns.bushelOnly;
                break;
            default:
                throw new Error("Unrecognized offer status");
        };

        if (contractConfig.preferredSortColumnId && contractConfig.preferredSortOrder
            && contractConfig.visibleColumns.includes(contractConfig.preferredSortColumnId)) {
            initialSort.colId = contractConfig.preferredSortColumnId;
            initialSort.sortOrder = contractConfig.preferredSortOrder as SortDirection
        };

        setSelectedSortForStatus(initialSort);
        gridRef.current.columnApi.applyColumnState({
            state: [{
                colId: initialSort.colId,
                sort: initialSort.sortOrder
            }]
        });
    }

    function isLoaded(): boolean {
        return myCommodities != null && erpIntegrationLoader.isLoaded;
    }

    if (!isLoaded()) return <></>;

    return <div ref={componentRef} className="contract-table w-100">
        <StandardGrid ref={gridRef}
            {...props.gridOptions}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            suppressMultiSort
            rowData={props.contracts}
            quickFilterText={props.search}
            paginationPageSize={25}
            pagination
            getRowId={getRowId}
            getContextMenuItems={getContextMenuItems}
            context={{ allowContractPricing }}
            popupParent={document.querySelector("body")}
            onRowDataUpdated={handleRowDataUpdated}
            onGridColumnsChanged={handleGridColumnsChanged}
            onGridReady={handleGridReady}
            onSortChanged={onSortChanged}
        />

        {resyncState.isShowing && <SyncContractToErpRetryModal modalState={resyncState} />}

        <ContractActions {...contractActionMenu} />
    </div>;
}

const getRowId = (params: any) => params.data.id;
const handleRowDataUpdated = (params: any) => params.columnApi.autoSizeColumn("quantity");
const handleGridColumnsChanged = (params: any) => {
    params.api.sizeColumnsToFit();
    params.columnApi.autoSizeColumn("quantity");
};

export default ContractTable;

function setSortOrderForType(config: ContractColumnConfigurations, sort: SelectedSort) {

    const newVals = {
        preferredSortColumnId: sort.colId,
        preferredSortOrder: sort.sortOrder
    };
    switch (sort.type) {
        case ContractTableType.Basis:
            config.basis = { ...config.basis, ...newVals };
            break;

        case ContractTableType.Cash:
            config.cash = { ...config.cash, ...newVals };
            break;

        case ContractTableType.Hta:
            config.hta = { ...config.hta, ...newVals };
            break;

        case ContractTableType.DelayedPrice:
            config.delayedPrice = { ...config.delayedPrice, ...newVals };
            break;

        case ContractTableType.BushelOnly:
            config.bushelOnly = { ...config.bushelOnly, ...newVals };
            break;

        case ContractTableType.WithOptions:
            config.withOptions = { ...config.withOptions, ...newVals };
            break;

        default:
            throw new Error("Unrecognized contract type");
    }
}

export function getContractTableTypeName(contractType: ContractTableType): string {
    switch (contractType) {
        case ContractTableType.Cash:
            return localize("Domain_Value_Cash");
        case ContractTableType.Hta:
            return localize("Domain_Value_Hta");
        case ContractTableType.Basis:
            return localize("Domain_Value_Basis");
        case ContractTableType.DelayedPrice:
            return localize("Domain_Value_DelayedPrice");
        case ContractTableType.BushelOnly:
            return localize("Domain_Value_BushelOnly");
        case ContractTableType.WithOptions:
            return localize("Domain_Value_WithOptions");
        default:
            return localize("ContractTable_Value_Unknown");
    }
}